import React, { useMemo } from 'react';
import { defineMessages } from 'react-intl';
import { generatePath, useLocation, useParams } from 'react-router-dom';
import PageWrapper from '@components/common/layout/PageWrapper';
import apiConfig from '@constants/apiConfig';
import { errorCodes } from '@constants/ErrorCode';
import useSaveBase from '@hooks/useSaveBase';
import useTranslate from '@hooks/useTranslate';
import { commonMessage } from '@locales/intl';
import routes from '@routes';
import { showErrorMessage } from '@services/notifyService';

import PickupDisplayDeviceForm from './PickupDisplayDeviceForm';

const message = defineMessages({
    objectName: 'ReadyBoard mapping',
});

const PickupDisplayDeviceSavePage = () => {
    const translate = useTranslate();
    const params = useParams();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    
    const tab = queryParams.get('tab');
    const parentId = queryParams.get('parentId');
    const restaurantId = queryParams.get('restaurantId');
    const restaurantName = queryParams.get('restaurantName');
    const collapsePickUpId = queryParams.get('collapsePickUpId');
    const collapseParam = collapsePickUpId ? `&collapsePickUpId=${collapsePickUpId}` : '';

    const { detail, mixinFuncs, loading, setIsChangedFormValues, isEditing, title, setSubmit } = useSaveBase({
        apiConfig: {
            getById: apiConfig.orderDisplay.getById,
            create: apiConfig.orderDisplay.create,
            update: apiConfig.orderDisplay.update,
        },
        options: {
            getListUrl: generatePath(routes.deviceTabsRestaurantListPage.path, { customerId: params.customerId }) + `?tab=${tab}&restaurantId=${restaurantId}&restaurantName=${restaurantName}${collapseParam}`,
            objectName: translate.formatMessage(message.objectName),
        },
        override: (funcs) => {
            funcs.prepareUpdateData = (data) => {
                return {
                    ...data,
                    id: detail.id,
                    restaurantId,
                    parentId,
                };
            };
            funcs.prepareCreateData = (data) => {
                return {
                    ...data,
                    restaurantId,
                    parentId,
                };
            };
            funcs.onSaveError = (err) => {
                const errorCode = err.response?.data?.code;
                const errorMessage = errorCodes[errorCode]?.message;
                if (errorMessage) {
                    showErrorMessage(translate.formatMessage(errorMessage), translate);
                    setSubmit(false);
                } else {
                    mixinFuncs.handleShowErrorMessage(err, showErrorMessage);
                    setSubmit(false);
                }
            };
        },
    });

    const dataDetail = useMemo(
        () =>
            detail?.device
                ? {
                    ...detail.device,
                    layout: detail?.layout,
                }
                : {},
        [detail],
    );

    return (
        <PageWrapper
            loading={loading}
            routes={[
                {
                    breadcrumbName: translate.formatMessage(commonMessage.digibesShop),
                    path: routes.shopAccountListPage.path,
                },
                {
                    breadcrumbName: translate.formatMessage(commonMessage.restaurant),
                    path: generatePath(routes.RestaurantByCustomerListPage.path,{ customerId: params.customerId }),
                },
                {
                    breadcrumbName: restaurantName || translate.formatMessage(commonMessage.devices),
                    path: `${generatePath(routes.deviceTabsRestaurantListPage.path, {
                        customerId: params.customerId,
                    })}?tab=${tab}&restaurantId=${restaurantId}&restaurantName=${restaurantName}&collapsePickUpId=${collapsePickUpId}`,
                },
                { breadcrumbName: title },
            ]}
            title={title}
        >
            <PickupDisplayDeviceForm
                setIsChangedFormValues={setIsChangedFormValues}
                dataDetail={dataDetail}
                formId={mixinFuncs.getFormId()}
                isEditing={isEditing}
                actions={mixinFuncs.renderActions()}
                onSubmit={mixinFuncs.onSave}
            />
        </PageWrapper>
    );
};

export default PickupDisplayDeviceSavePage;
